'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, ScanLine } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useUser } from '@/lib/useUser';
import { getPlayerEasterEgg } from '@/lib/playerEasterEggs';

type Message = {
  id: string;
  user: string;
  text: string;
  time: string;
};

export default function LanChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [availablePlayers, setAvailablePlayers] = useState<string[]>([]);
  const [easterEgg, setEasterEgg] = useState<{ player: string; text: string } | null>(null);
  const { username, updateUsername } = useUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  const easterEggTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMessages = async () => {
    try {
      const res = await fetch('/api/chat');
      const data = await res.json();
      setMessages(data);
    } catch (e) { console.error("Chat Error:", e); }
  };

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const res = await fetch('/api/users');
        const data = await res.json();
        setAvailablePlayers(data);
      } catch {}
    };
    void Promise.resolve().then(fetchPlayers);
    void Promise.resolve().then(fetchMessages);
    const interval = setInterval(() => {
      fetchMessages();
      fetchPlayers(); // Keep user list updated
    }, 2000); 
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => () => {
    if (easterEggTimer.current) clearTimeout(easterEggTimer.current);
  }, []);

  const selectIdentity = (player: string) => {
    updateUsername(player);
    setEasterEgg({ player, text: getPlayerEasterEgg(player) });

    if (easterEggTimer.current) clearTimeout(easterEggTimer.current);
    easterEggTimer.current = setTimeout(() => setEasterEgg(null), 6500);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Optimistic UI update
    const tempMsg = { id: 'temp', user: username, text: input, time: new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) };
    setMessages([...messages, tempMsg]);
    
    await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user: username, text: input })
    });

    setInput('');
    fetchMessages();
  };

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl flex flex-col h-full shadow-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="text-green-500 w-5 h-5" />
          <h2 className="text-white font-bold uppercase tracking-wider text-sm">Chat</h2>
        </div>
        
        {/* Username Dropdown with Identifier */}
        <div className="flex items-center gap-2">
          {username === 'Commander' && (
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
          )}
          <select 
            value={username}
            onChange={(e) => selectIdentity(e.target.value)}
            className={`bg-slate-900 border ${username === 'Commander' ? 'border-red-500 animate-pulse' : 'border-slate-700'} rounded px-2 py-1 text-[10px] text-slate-300 w-28 text-center focus:border-green-500 outline-none cursor-pointer appearance-none hover:bg-slate-800 transition-colors uppercase font-bold`}
            title="Identität wählen"
          >
            {username === 'Commander' && <option value="Commander">-- WÄHLEN --</option>}
            {availablePlayers.map(player => (
              <option key={player} value={player}>{player.toUpperCase()}</option>
            ))}
            {!availablePlayers.includes(username) && username !== 'Commander' && (
               <option value={username}>{username.toUpperCase()} (GAST)</option>
            )}
          </select>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {easterEgg && (
          <motion.div
            key={`${easterEgg.player}-${easterEgg.text}`}
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            className={`overflow-hidden border-b ${easterEgg.player === 'Patrick' ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200' : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
          >
            <div className="px-4 py-3 flex items-start gap-3">
              <ScanLine className="w-5 h-5 shrink-0 mt-0.5 animate-pulse" />
              <div>
                <p className="text-[9px] font-black tracking-[0.22em] uppercase opacity-70 mb-1">
                  Persönliche Systemdiagnose // {easterEgg.player}
                </p>
                <p className="text-xs font-bold leading-relaxed">{easterEgg.text}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar bg-slate-900/50">
        {messages.length === 0 && (
          <p className="text-center text-slate-600 text-xs italic mt-10">Funkstille...</p>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex flex-col ${msg.user === username ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[85%] rounded-lg p-2 text-sm ${
              msg.user === 'System' ? 'bg-slate-800 text-slate-400 text-center w-full italic' :
              msg.user === username ? 'bg-green-600/20 text-green-100 border border-green-600/30' : 'bg-slate-800 text-slate-200 border border-slate-700'
            }`}>
              {msg.user !== 'System' && (
                <div className="flex justify-between items-baseline gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase ${msg.user === username ? 'text-green-400' : 'text-blue-400'}`}>{msg.user}</span>
                  <span className="text-[9px] text-slate-500">{msg.time}</span>
                </div>
              )}
              <p className="leading-tight">{msg.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 bg-slate-950 border-t border-slate-800 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Nachricht an alle..."
          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-green-500 transition-colors"
        />
        <button 
          onClick={sendMessage}
          className="bg-green-600 hover:bg-green-500 text-white p-2 rounded-lg transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
